import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Car, Search, X } from "lucide-react";
import { api, money, garageVatRate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CustomerPicker } from "@/components/CustomerPicker";
import { PartPicker } from "@/components/PartPicker";
import { NumberPlate } from "@/components/NumberPlate";
import { InvoiceLineItems } from "@/components/InvoiceLineItems";
import { INVOICE_CATEGORIES, CATEGORY_LABEL } from "@/lib/invoiceMeta";
import { toast } from "sonner";

export default function InvoiceNew() {
  const navigate = useNavigate();
  const [catQuery, setCatQuery] = useState("");
  const [categories, setCategories] = useState([]); // array of ids
  const [customer, setCustomer] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [title, setTitle] = useState("");
  const [vatEnabled, setVatEnabled] = useState(true);
  const [garage, setGarage] = useState(null);
  const [discount, setDiscount] = useState("");
  const [discountType, setDiscountType] = useState("amount");
  const [items, setItems] = useState([{ description: "", qty: 1, unit_price: "", kind: "labour", category: "" }]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Inline add-vehicle
  const [addingVeh, setAddingVeh] = useState(false);
  const [vrm, setVrm] = useState("");
  const [vehForm, setVehForm] = useState({ make: "", model: "", year: "", colour: "" });
  const [looking, setLooking] = useState(false);
  const [vehSaving, setVehSaving] = useState(false);

  useEffect(() => {
    api("/vehicles").then(setVehicles).catch(() => {});
    api("/garage/settings").then((g) => { setGarage(g); setVatEnabled(garageVatRate(g) > 0); }).catch(() => {});
  }, []);

  const catResults = useMemo(() => {
    const q = catQuery.trim().toLowerCase();
    return INVOICE_CATEGORIES.filter((c) => !categories.includes(c.id) && (!q || c.label.toLowerCase().includes(q)));
  }, [catQuery, categories]);
  const selectedCats = useMemo(() => categories.map((id) => ({ id, label: CATEGORY_LABEL[id] || id })), [categories]);
  const addCat = (id) => { setCategories((p) => (p.includes(id) ? p : [...p, id])); setCatQuery(""); };
  const removeCat = (id) => { setCategories((p) => p.filter((c) => c !== id)); setItems((prev) => prev.map((it) => (it.category === id ? { ...it, category: "" } : it))); };

  const customerVehicles = useMemo(
    () => (customer ? vehicles.filter((v) => v.customer_id === customer.id) : []),
    [vehicles, customer]
  );
  const onCustomer = (c) => { setCustomer(c); setVehicleId(""); setAddingVeh(false); };

  const lookup = async () => {
    if (!vrm.trim()) return;
    setLooking(true);
    try {
      const d = await api(`/dvla/lookup/${encodeURIComponent(vrm.trim())}`);
      setVehForm({ make: d.make || "", model: d.model || "", year: d.year || "", colour: d.colour || "" });
      toast.success("DVLA details loaded");
    } catch (e) { toast.error(e.message || "Vehicle not found"); } finally { setLooking(false); }
  };
  const saveVehicle = async () => {
    if (!vrm.trim() || !vehForm.make.trim() || !vehForm.model.trim()) { toast.error("VRM, make and model are required"); return; }
    setVehSaving(true);
    try {
      const v = await api("/vehicles", { method: "POST", body: JSON.stringify({
        vrm: vrm.trim(), make: vehForm.make.trim(), model: vehForm.model.trim(),
        year: vehForm.year ? Number(vehForm.year) : null, colour: vehForm.colour || null, customer_id: customer.id,
      }) });
      setVehicles((p) => [v, ...p]); setVehicleId(v.id);
      setAddingVeh(false); setVrm(""); setVehForm({ make: "", model: "", year: "", colour: "" });
      toast.success("Vehicle added");
    } catch (e) { toast.error(e.message || "Could not add vehicle"); } finally { setVehSaving(false); }
  };

  const netRaw = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const discAmt = Number(discount) > 0 ? (discountType === "percent" ? netRaw * Number(discount) / 100 : Number(discount)) : 0;
  const net = Math.max(0, netRaw - discAmt);
  const vatPct = garageVatRate(garage);
  const vat = vatEnabled && vatPct > 0 ? net * (vatPct / 100) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!customer) { toast.error("Select or add a customer"); return; }
    const clean = items.filter((i) => i.description.trim());
    if (!clean.length) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      const inv = await api("/invoices", { method: "POST", body: JSON.stringify({
        customer_id: customer.id, vehicle_id: vehicleId || null,
        categories, title: title.trim() || selectedCats[0]?.label || "Invoice",
        vat_enabled: vatEnabled,
        line_items: clean.map((i) => ({
          description: i.description, qty: Number(i.qty) || 0, unit_price: Number(i.unit_price) || 0,
          kind: i.kind || "labour", category: i.category || null,
          ...(i.hours != null && i.hours !== "" ? { hours: Number(i.hours) || 0 } : {}),
          ...(i.hourly_rate != null && i.hourly_rate !== "" ? { hourly_rate: Number(i.hourly_rate) || 0 } : {}),
        })),
        discount: Number(discount) || 0, discount_type: discountType,
      }) });
      toast.success(`Invoice ${inv.invoice_number} created`);
      navigate(`/invoice/${inv.id}`);
    } catch (e2) { toast.error(e2.message); } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <Link to="/money" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Finance</Link>
      <PageHeader title="New Invoice" subtitle="Pick work categories, a customer and add items" />
      <form onSubmit={submit} className="space-y-5 rounded-xl border border-border bg-card p-6">
        {/* Work categories (multi-select) */}
        <div data-testid="invoice-category-section">
          <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">WORK CATEGORIES</span>
          {selectedCats.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5" data-testid="invoice-categories-selected">
              {selectedCats.map((c) => (
                <span key={c.id} data-testid={`invoice-cat-chip-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-brand-tertiary px-2.5 py-1 text-xs font-bold text-brand-2">
                  {c.label}
                  <button type="button" onClick={() => removeCat(c.id)} data-testid={`invoice-cat-remove-${c.id}`} className="text-brand-2"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="rounded-lg border border-border">
            <div className="flex items-center gap-2 border-b border-border px-3">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input value={catQuery} onChange={(e) => setCatQuery(e.target.value)} data-testid="invoice-category-search"
                placeholder="Search & add categories (Engine, Brakes, MOT…)" className="w-full bg-transparent py-3 text-sm outline-none" />
            </div>
            <div className="flex max-h-44 flex-wrap gap-1.5 overflow-y-auto p-2">
              {catResults.length === 0 && <p className="px-2 py-2 text-sm text-muted-foreground">No more categories.</p>}
              {catResults.map((c) => (
                <button type="button" key={c.id} onClick={() => addCat(c.id)} data-testid={`invoice-cat-add-${c.id}`}
                  className="rounded-full border border-border px-2.5 py-1 text-xs text-slate-600 hover:border-brand hover:text-brand-2">+ {c.label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Customer */}
        <div>
          <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">CUSTOMER *</span>
          <CustomerPicker value={customer} onChange={onCustomer} />
        </div>

        {/* Vehicle */}
        {customer && (
          <div data-testid="invoice-vehicle-section">
            <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">VEHICLE (optional)</span>
            {!addingVeh ? (
              <div className="space-y-2">
                {customerVehicles.length > 0 ? (
                  <div className="space-y-2">
                    {customerVehicles.map((v) => (
                      <button key={v.id} type="button" onClick={() => setVehicleId(vehicleId === v.id ? "" : v.id)} data-testid={`invoice-veh-${v.id}`}
                        className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left ${vehicleId === v.id ? "border-brand bg-brand-tertiary" : "border-border bg-card hover:bg-secondary"}`}>
                        <NumberPlate vrm={v.vrm} size="sm" />
                        <span className="text-sm font-semibold text-midnight">{v.make} {v.model}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-lg bg-secondary px-3 py-2.5 text-sm text-muted-foreground">No vehicles for this customer yet.</p>
                )}
                <button type="button" onClick={() => setAddingVeh(true)} data-testid="invoice-add-vehicle-toggle"
                  className="inline-flex items-center gap-2 rounded-lg bg-brand-tertiary px-3 py-2 text-sm font-bold text-brand-2"><Car className="h-4 w-4" />Add vehicle</button>
              </div>
            ) : (
              <div className="space-y-2 rounded-lg border border-border p-3">
                <div className="flex gap-2">
                  <input value={vrm} onChange={(e) => setVrm(e.target.value.toUpperCase())} placeholder="AB12 XYZ" data-testid="invoice-new-veh-vrm"
                    className="min-w-0 flex-1 rounded-md border border-input bg-[#FCD34D] px-3 py-2.5 text-center text-lg font-extrabold tracking-widest text-black outline-none" />
                  <button type="button" onClick={lookup} disabled={looking} data-testid="invoice-new-veh-lookup"
                    className="inline-flex items-center gap-1 rounded-md bg-midnight px-3 py-2 text-sm font-bold text-white disabled:opacity-60">{looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Lookup</button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input value={vehForm.make} onChange={(e) => setVehForm({ ...vehForm, make: e.target.value })} placeholder="Make *" className="rounded-md border border-input px-2.5 py-2 text-sm outline-none focus:border-brand" />
                  <input value={vehForm.model} onChange={(e) => setVehForm({ ...vehForm, model: e.target.value })} placeholder="Model *" className="rounded-md border border-input px-2.5 py-2 text-sm outline-none focus:border-brand" />
                  <input value={vehForm.year} onChange={(e) => setVehForm({ ...vehForm, year: e.target.value })} placeholder="Year" className="rounded-md border border-input px-2.5 py-2 text-sm outline-none focus:border-brand" />
                  <input value={vehForm.colour} onChange={(e) => setVehForm({ ...vehForm, colour: e.target.value })} placeholder="Colour" className="rounded-md border border-input px-2.5 py-2 text-sm outline-none focus:border-brand" />
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={saveVehicle} disabled={vehSaving} data-testid="invoice-new-veh-save" className="flex flex-1 items-center justify-center gap-1 rounded-md bg-brand py-2 text-sm font-bold text-white disabled:opacity-60">{vehSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Save vehicle</button>
                  <button type="button" onClick={() => setAddingVeh(false)} className="rounded-md border border-border px-3 py-2 text-sm font-semibold">Back</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">TITLE</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="invoice-title" placeholder={selectedCats[0]?.label || "Invoice"} className="w-full rounded-lg border border-input px-3.5 py-3 text-base outline-none focus:border-brand" /></label>
          {vatPct > 0 ? (
            <label className="flex items-end gap-2 pb-2">
              <input type="checkbox" checked={vatEnabled} onChange={(e) => setVatEnabled(e.target.checked)} data-testid="invoice-new-vat-toggle" className="h-5 w-5 rounded border-input" />
              <span className="text-sm font-bold text-midnight">VAT invoice {vatEnabled ? `(${vatPct}%)` : "(no VAT)"}</span>
            </label>
          ) : (
            <p className="flex items-end pb-2 text-xs text-muted-foreground" data-testid="invoice-new-vat-off">VAT is off for this garage (Account &amp; Settings) — no VAT will be added.</p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">DISCOUNT</span>
              <input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="invoice-new-discount" className="w-full rounded-lg border border-input px-3 py-2.5 text-sm outline-none focus:border-brand" /></label>
            <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">TYPE</span>
              <select value={discountType} onChange={(e) => setDiscountType(e.target.value)} data-testid="invoice-new-discount-type" className="w-full rounded-lg border border-input px-3 py-2.5 text-sm outline-none focus:border-brand"><option value="amount">£ Amount</option><option value="percent">% Percent</option></select></label>
          </div>
        </div>

        <InvoiceLineItems items={items} setItems={setItems} categories={selectedCats} onAddFromStock={() => setPickerOpen(true)} />

        <div className="flex justify-end gap-6 border-t border-border pt-3 text-sm">
          <span className="text-muted-foreground">Sub Total {money(netRaw)}</span>
          {discAmt > 0 && <span className="text-muted-foreground">Disc -{money(discAmt)}</span>}
          {vatEnabled && <span className="text-muted-foreground">Tax {money(vat)}</span>}
          <span className="font-display text-lg font-bold text-midnight">{money(net + vat)}</span>
        </div>

        <button type="submit" disabled={saving} data-testid="invoice-new-save-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-wide text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "CREATE INVOICE"}
        </button>
      </form>
      <PartPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={(p) => setItems((prev) => { const base = prev.filter((it) => it.description.trim() || it.unit_price); return [...base, { description: p.name, qty: 1, unit_price: Number(p.unit_price) || 0, kind: "part", category: "" }]; })} />
    </div>
  );
}
